"use client";

import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";

interface StepFooterProps {
  readonly onBack?: () => void;
  readonly onSkip?: () => void;
  readonly onNext: () => void;
  /** Label for the primary (right-most) button. Defaults to t("wizard.next"). */
  readonly nextLabel?: string;
  /** Custom content to render inside the primary button instead of nextLabel text. */
  readonly nextContent?: ReactNode;
  /** Disables the primary button when true. */
  readonly nextDisabled?: boolean;
  /** Disables the back button when true. */
  readonly backDisabled?: boolean;
  /** Disables the skip button when true. */
  readonly skipDisabled?: boolean;
  /** Shows a saving state label. When true, overrides nextLabel with t("wizard.saving"). */
  readonly isSaving?: boolean;
  /** Label for the skip button. Defaults to t("wizard.skipForNow"). */
  readonly skipLabel?: string;
  /** When true, the skip button gets ml-auto even if there is no back button. Defaults to true. */
  readonly skipAutoMargin?: boolean;
  /** Optional validation error message displayed above the button row. */
  readonly validationError?: string;
  /** Override the outer background class. Defaults to "bg-panel_secondary/50". */
  readonly bgClassName?: string;
}

export function StepFooter({
  onBack,
  onSkip,
  onNext,
  nextLabel,
  nextContent,
  nextDisabled,
  backDisabled,
  skipDisabled,
  isSaving,
  skipLabel,
  skipAutoMargin = true,
  validationError,
  bgClassName = "bg-panel_secondary/50",
}: StepFooterProps) {
  const { t } = useTranslation("onboarding");

  const resolvedNextLabel = isSaving ? t("wizard.saving") : (nextLabel ?? t("wizard.next"));

  const hasValidationError = Boolean(validationError);

  return (
    <div
      className={clsx(
        "sticky bottom-0 flex",
        hasValidationError ? "flex-col items-center gap-2" : "items-center gap-4",
        "px-8 py-4",
        bgClassName,
        "border-t border-200 backdrop-blur-md"
      )}
    >
      {hasValidationError && <p className="text-sm text-destructive">{validationError}</p>}
      {hasValidationError ? (
        <div className="w-full flex items-center gap-4">
          <StepFooterButtons
            onBack={onBack}
            onSkip={onSkip}
            onNext={onNext}
            resolvedNextLabel={resolvedNextLabel}
            nextContent={nextContent}
            nextDisabled={nextDisabled}
            backDisabled={backDisabled}
            skipDisabled={skipDisabled}
            isSaving={isSaving}
            skipLabel={skipLabel}
            skipAutoMargin={skipAutoMargin}
            t={t}
          />
        </div>
      ) : (
        <StepFooterButtons
          onBack={onBack}
          onSkip={onSkip}
          onNext={onNext}
          resolvedNextLabel={resolvedNextLabel}
          nextContent={nextContent}
          nextDisabled={nextDisabled}
          backDisabled={backDisabled}
          skipDisabled={skipDisabled}
          isSaving={isSaving}
          skipLabel={skipLabel}
          skipAutoMargin={skipAutoMargin}
          t={t}
        />
      )}
    </div>
  );
}

/** Internal helper that renders back / skip / next buttons. */
function StepFooterButtons({
  onBack,
  onSkip,
  onNext,
  resolvedNextLabel,
  nextContent,
  nextDisabled,
  backDisabled,
  skipDisabled,
  isSaving,
  skipLabel,
  skipAutoMargin,
  t,
}: Readonly<{
  onBack?: () => void;
  onSkip?: () => void;
  onNext: () => void;
  resolvedNextLabel: string;
  nextContent?: ReactNode;
  nextDisabled?: boolean;
  backDisabled?: boolean;
  skipDisabled?: boolean;
  isSaving?: boolean;
  skipLabel?: string;
  skipAutoMargin: boolean;
  t: (key: string) => string;
}>) {
  return (
    <>
      {onBack && (
        <Button
          type="tertiary"
          className="min-w-[120px]"
          onClick={onBack}
          disabled={backDisabled || isSaving}
        >
          {t("wizard.back")}
        </Button>
      )}
      {onSkip && (
        <Button
          type="tertiary"
          className={clsx("min-w-[120px]", skipAutoMargin && "ml-auto")}
          onClick={onSkip}
          disabled={skipDisabled || isSaving}
        >
          {skipLabel ?? t("wizard.skipForNow")}
        </Button>
      )}
      <Button
        type="primary"
        className={clsx("min-w-[120px]", !onSkip && "ml-auto")}
        onClick={onNext}
        disabled={nextDisabled || isSaving}
      >
        {nextContent ?? resolvedNextLabel}
      </Button>
    </>
  );
}
